/**
 * Auth Prompt Modal
 * Shows authentication UI when user attempts an action requiring sign-in
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import FocusTrap from 'focus-trap-react';
import { useAuth } from '@/context';
import { AuthUI } from '../AuthUI';
import type { AuthView } from '../AuthUI';
import styles from './AuthPrompt.module.css';
import type { AuthPromptProps } from './AuthPrompt.types';

const VIEW_TITLES: Record<AuthView, string> = {
  sign_in: 'Sign In',
  sign_up: 'Create Account',
  forgotten_password: 'Reset Password',
};

export function AuthPrompt({
  isOpen,
  onClose,
  onSuccess,
  message = 'Please sign in to continue',
  showFreeCreditsCallout = false,
  title,
}: AuthPromptProps) {
  const { user } = useAuth();
  const prevUserRef = useRef(user);
  const hasHandledSuccessRef = useRef(false);
  const [currentView, setCurrentView] = useState<AuthView>('sign_in');

  const displayTitle = title ?? VIEW_TITLES[currentView];

  // Close on Escape key
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    },
    [isOpen, onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset success handler when modal opens (not when user changes)
  useEffect(() => {
    if (isOpen) {
      hasHandledSuccessRef.current = false;
      // Capture the user state at the moment the modal opens
      // This allows us to detect the null → user transition
      prevUserRef.current = user;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]); // Only run when modal opens/closes, NOT when user changes

  // When user becomes authenticated, call onSuccess and close (only once)
  useEffect(() => {
    // Only trigger if:
    // 1. Modal is open
    // 2. User just became authenticated (transition from null to user)
    // 3. We haven't already handled this success
    const userJustSignedIn = !prevUserRef.current && user;

    if (isOpen && userJustSignedIn && !hasHandledSuccessRef.current) {
      hasHandledSuccessRef.current = true;
      onSuccess?.();
      onClose();
    }

    // Update previous user reference
    prevUserRef.current = user;
  }, [isOpen, user, onSuccess, onClose]);

  // Close on overlay click
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <FocusTrap
      active={isOpen}
      focusTrapOptions={{
        allowOutsideClick: true,
        escapeDeactivates: true,
        clickOutsideDeactivates: true,
      }}
    >
      <div
        className={styles.overlay}
        onClick={handleOverlayClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-prompt-title"
      >
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2 id="auth-prompt-title" className={styles.title}>
              {displayTitle}
            </h2>
            <button
              type="button"
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close dialog"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          <div className={styles.content}>
            {showFreeCreditsCallout && (
              <div className={styles.freeCreditsCallout}>
                <span className={styles.freeCreditsTitle}>5 Free AI Designs</span>
                <span className={styles.freeCreditsSubtext}>No credit card required</span>
              </div>
            )}
            {message && <p className={styles.message}>{message}</p>}
            <div className={styles.authContainer}>
              <AuthUI onViewChange={setCurrentView} />
            </div>
          </div>
        </div>
      </div>
    </FocusTrap>,
    document.body
  );
}
